
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getPioneerSummary,
  recordPioneerSummaryEvent,
} from '../services/pioneerSummaryApi';

import backIcon from '../assets/backspace-icon.png';
import tuneIcon from '../assets/tune-icon.png';
import { BADGE_DISPLAY } from '../constants/badges';
import placeholderProfile from '../assets/placeholder-profile.png';
import './PioneerSummaryPanel.css';

const BADGE_ROUTES = {
  BARTER_KING: '/badges/barter-king/',
  MERCHANT_STAR: '/badges/merchant-star/',
  CREATOR_FLAME: '/badges/creator-flame/',
  PI_GUARDIAN: '/badges/pi-guardian/',
};



function monthYear(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function postLabel(value) {
  if (value === 'recent_post') return 'Recently shared';
  if (value === 'notable_post') return 'A post people engaged with';
  return 'Post';
}


function explorerGroupLabel(group) {
  if (group === 'self') return 'Your summary';
  if (group === 'following') return 'Following';
  if (group === 'follower') return 'Follows you';
  if (group === 'badge_holder') return 'Badge holder';
  return 'Suggested Pioneer';
}

export default function PioneerSummaryPanel({
  username,
  entryPoint,
  onClose,
  onFollow,
  onMessage,
  onOpenFilters,
  filterActive = false,
  explorer, // Optional: { group, position, total }
  embedded = false,
}) {

  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    setLoading(true);
    setError('');
    setSummary(null);

    getPioneerSummary(username)
      .then((data) => {
        if (alive) setSummary(data);
      })
      .catch((err) => {
        if (!alive) return;

        setError(
          err?.response?.status === 403
            ? 'This summary is unavailable.'
            : 'Could not load this summary. Please try again.'
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [username]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [username]);


useEffect(() => {
  if (embedded) return undefined;

  const oldOverflow = document.body.style.overflow; 
   document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = oldOverflow;
      document.removeEventListener('keydown', handleEscape);
    };

}, [onClose, embedded]);



const trackEvent = (eventType, extra = {}) => {
  if (!entryPoint) return;

  recordPioneerSummaryEvent(
    username,
    eventType,
    entryPoint,
    extra
  ).catch((err) => {
    console.warn(
      'Summary event unavailable:',
      err?.response?.status || err?.message
    );
  });
};


  const openDestination = (path) => {
    onClose?.();
    navigate(path);
  };


  const profile = summary?.profile;
  const displayRole = profile?.displayRole || 'Pioneer';

  const count =
    summary?.activity?.find(
      (item) => item.type === 'original_posts'
    )?.count || 0;

const badges = Array.isArray(summary?.badges)
  ? summary.badges
  : [];
 
 const actions = summary?.actions;

  return (
    <div
      className="pioneer-summary-screen"
      role="dialog"
      aria-modal="true"
      aria-label="Pioneer summary"
    >
      <header className="pioneer-summary-header">
        <button
          type="button"
          className="pioneer-summary-back"
          onClick={onClose}
          aria-label="Close summary"
        >
          <img src={backIcon} alt="" />
        </button>

        <h1>Pioneer summary</h1>


        {explorer && onOpenFilters ? (
          <button
            type="button"
            className={`pioneer-summary-filter-button${
              filterActive ? ' is-active' : ''
            }`}
            onClick={onOpenFilters}
            aria-label={
              filterActive
                ? 'Filter Pioneers, filter active'
                : 'Filter Pioneers'
            }
          >
            <img src={tuneIcon} alt="" />
          </button>
        ) : (
          <span
            className="pioneer-summary-header-space"
            aria-hidden="true"
          />
        )}

      </header>

      {explorer && (
        <div className="pioneer-explorer-context">
          <span className="pioneer-explorer-group">
            {explorerGroupLabel(explorer.group)}
          </span>

          <span className="pioneer-explorer-position">
            {explorer.position} of {explorer.total}
          </span>
        </div>
      )}


<main ref={scrollRef} className="pioneer-summary-scroll">

        {loading && (
          <div className="pioneer-summary-state" role="status">
            Getting to know this Pioneer…
          </div>
        )}

        {!loading && error && (
          <div className="pioneer-summary-state" role="alert">
            {error}
          </div>
        )}

        {!loading && profile && (
          <div className="pioneer-summary-content">
            <section className="pioneer-summary-person">
              <img
                className="pioneer-summary-avatar"
                src={profile.profilePic || placeholderProfile}
                alt=""
                onError={(event) => {
                  event.currentTarget.src = placeholderProfile;
                }}
              />

              <h2>{profile.name || profile.username}</h2>



              <span
                className={`pioneer-summary-role-pill ${
                  displayRole === 'Pioneer' ? 'is-pioneer' : ''
                }`}
              >
                {displayRole}
              </span>

              {profile.joinedAt && (
                <p className="pioneer-summary-joined">
                  On BarterFi since {monthYear(profile.joinedAt)}
                </p>
              )}

              {profile.bio && (
                <p className="pioneer-summary-bio">
                  {profile.bio}
                </p>
              )}

              {!!profile.languages?.length && (
                <div
                  className="pioneer-summary-languages"
                  aria-label="Languages added to profile"
                >
                  {profile.languages.map((language) => (
                    <span key={language}>{language}</span>
                  ))}
                </div>
              )}
            </section>

            <section className="pioneer-summary-section">
              <h3>What they do here</h3>

              <p className="pioneer-summary-activity">
                {count > 0
                  ? `Shared ${count} original ${
                      count === 1 ? 'post' : 'posts'
                    } in the last 30 days.`
                  : 'No original posts in the last 30 days.'}
              </p>
            </section>

            <section className="pioneer-summary-section">
              <h3>Explore their activity</h3>

{!summary.highlights?.length && badges.length === 0 && (

                <p className="pioneer-summary-empty">
                  There are no highlights to explore yet.
                </p>
              )}

              {(summary.highlights || []).map((item) => (
                <button
                  key={`${item.type}-${item.postId}`}
                  type="button"
                  className="pioneer-summary-highlight"
  
onClick={() => {
  trackEvent('highlight_opened', {
    highlightType: 'post',
    sourceId: String(item.postId),
  });

  openDestination(
    `/post/${encodeURIComponent(item.postId)}`
  );
}}


                >
                  <span className="pioneer-summary-highlight-label">
                    {postLabel(item.label)}
                  </span>

                  <span className="pioneer-summary-highlight-text">
                    {item.excerpt || 'View this post'}
                  </span>

                  <span className="pioneer-summary-highlight-action">
                    Read post →
                  </span>
                </button>
              ))}





{badges.length > 0 && (
  <div className="pioneer-summary-badges">
    <h4>Badges</h4>

    <div
      className="pioneer-summary-badges-row"
      role="group"
      aria-label="Current badges"
    >
      {badges.map((badge, index) => {
        const display = BADGE_DISPLAY[badge.type];
        const route = BADGE_ROUTES[badge.type];

        const label =
          display?.label ||
          String(badge.type || 'Badge').replace(/_/g, ' ');

        return (
          <button
            key={`${badge.type}-${index}`}
            type="button"
            className="pioneer-summary-badge-card"
            disabled={!route}
            onClick={() => {
              if (!route) return;

              trackEvent('highlight_opened', {
                highlightType: 'badge',
                sourceId: badge.type,
              });

              openDestination(
                `${route}${encodeURIComponent(profile.username)}`
              );
            }}
          >
            <span className="pioneer-summary-badge-name">
              {label}
            </span>

            {display?.icon ? (
              <img
                className="pioneer-summary-badge-icon"
                src={display.icon}
                alt=""
              />
            ) : (
              <span
                className="pioneer-summary-badge-emoji"
                aria-hidden="true"
              >
                {display?.emoji || '🏅'}
              </span>
            )}

            {route && (
              <span className="pioneer-summary-highlight-action">
                View badge →
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
)}


            </section>

            {!actions?.isOwner &&
              (actions?.canFollow || actions?.canMessage) && (
                <section className="pioneer-summary-section pioneer-summary-connect">
                  <h3>
                    Connect with {profile.name || profile.username}
                  </h3>

                  <div className="pioneer-summary-actions">
                    {actions.canFollow && onFollow && (
 

<button
  type="button"
  onClick={() => {
    if (!actions.isFollowing) {
      trackEvent('follow_clicked');
    }
    onFollow();
  }}
>
  {actions.isFollowing ? 'Following' : 'Follow'}
</button>

                    )}

                    {actions.canMessage && (
                      <button
                        type="button"


onClick={() => {
  trackEvent('message_clicked');

  if (onMessage) {
    onMessage();
  } else {
    openDestination(
      `/chat/${encodeURIComponent(
        profile.username
      )}`
    );
  }
}}


                      >
                        Message
                      </button>
                    )}
                  </div>
                </section>
              )}
          </div>
        )}
      </main>





      <footer className="pioneer-summary-footer">
        <div
          className="pioneer-summary-disabled-input"
          aria-disabled="true"
        >
          <span>Ask about this Pioneer</span>
          <span className="pioneer-summary-soon">
            Coming later
          </span>
        </div>
      </footer>
    </div>
  );
}
